import { Link } from "react-router-dom";
import { useT } from "../i18n/I18nProvider";
import { ChevronRight, AlertTriangle, ExternalLink } from "lucide-react";

// Anchor IDs used by the table of contents and by each section heading.
const TOC = [
  { id: "qui-peut",             label: "Qui peut entreprendre en Pologne" },
  { id: "jdg",                  label: "L'entreprise individuelle (JDG)" },
  { id: "spzoo",                label: "La société à responsabilité limitée (Sp. z o.o.)" },
  { id: "choisir",              label: "JDG ou Sp. z o.o. : comment choisir" },
  { id: "regimes-fiscaux",      label: "Les régimes fiscaux de l'indépendant" },
  { id: "zus",                  label: "Les cotisations sociales (ZUS)" },
  { id: "tva",                  label: "La TVA et la facturation électronique" },
  { id: "convention",           label: "La convention fiscale franco-polonaise" },
  { id: "residence-fiscale",    label: "La résidence fiscale" },
  { id: "premieres-demarches",  label: "Premières démarches concrètes" },
];

const KEY_POINTS = [
  "Les ressortissants de l'UE et de l'EEE, ainsi que les Suisses, peuvent créer une entreprise en Pologne aux mêmes conditions que les citoyens polonais. Les autres ressortissants dépendent de leur titre de séjour.",
  "Deux formes reviennent presque toujours : la JDG (entreprise individuelle, gratuite et rapide) et la Sp. z o.o. (l'équivalent de la SARL, à responsabilité limitée, capital minimal de 5 000 zł).",
  "L'indépendant choisit entre trois régimes fiscaux : le barème progressif (12 % puis 32 %), l'impôt linéaire (19 %) et le forfait sur le chiffre d'affaires (ryczałt, de 2 à 17 %). L'option pour le linéaire ou le forfait se déclare au plus tard le 20e jour du mois suivant le premier revenu de l'année, soit souvent le 20 février.",
  "Les cotisations ZUS sont largement forfaitaires : environ 1 927 zł par mois en régime plein en 2026 (cotisation santé en sus), avec un régime allégé pendant les deux premières années.",
  "La convention fiscale franco-polonaise de 1975 évite la double imposition entre la France et la Pologne, sur présentation d'un certificat de résidence fiscale.",
];

// 3 columns x 10 data rows
const CHOOSE_ROWS = [
  ["Personnalité juridique",      "Aucune, confondue avec la personne",          "Distincte de ses associés"],
  ["Responsabilité",              "Illimitée, sur le patrimoine personnel",      "Limitée aux apports (sauf exceptions)"],
  ["Capital minimal",             "Aucun",                                       "5 000 zł"],
  ["Registre",                    "CEIDG",                                       "KRS"],
  ["Coût de création",            "Gratuit",                                     "Environ 350 zł (S24) ou 600 zł plus notaire"],
  ["Délai",                       "Quelques jours",                              "De 24 heures (S24) à quelques semaines"],
  ["Comptabilité",                "Simplifiée",                                  "Complète, avec comptes annuels"],
  ["Imposition",                  "PIT (barème, linéaire ou ryczałt)",           "CIT 9 % ou 19 %, puis 19 % sur les dividendes"],
  ["Cotisations ZUS",             "Forfaitaires sur l'activité",                 "Aucune sur la détention de parts (sauf associé unique)"],
  ["Crédibilité, financement",    "Plus modeste",                                "Meilleure auprès des banques et des grands comptes"],
];

const SOURCES = [
  { url: "https://www.biznes.gov.pl/pl/portal/00806", label: "Biznes.gov.pl, créer une entreprise individuelle (CEIDG)" },
  { url: "https://www.ceidg.gov.pl", label: "CEIDG, registre des entreprises individuelles" },
  { url: "https://www.biznes.gov.pl", label: "Biznes.gov.pl, activité économique des étrangers en Pologne" },
  { url: "https://www.zus.pl", label: "ZUS, cotisations des entrepreneurs" },
  { url: "https://www.podatki.gov.pl", label: "Ministère des Finances polonais, impôts (PIT, CIT, VAT)" },
  { url: "https://ksef.podatki.gov.pl", label: "KSeF, système national de factures électroniques" },
  { url: "https://ekrs.ms.gov.pl", label: "Portail des registres du Tribunal (KRS et système S24)" },
  { url: "https://bofip.impots.gouv.fr/bofip/958-PGP.html", label: "BOFiP, convention fiscale France-Pologne" },
  { url: "https://www.impots.gouv.fr/sites/default/files/media/10_conventions/pologne/convention_avec_la_pologne_modifiee_par_la_cml.pdf", label: "Convention France-Pologne consolidée (texte avec l'instrument multilatéral)" },
  { url: "https://www.impots.gouv.fr", label: "Impots.gouv.fr, certificat de résidence fiscale" },
];

// Small reusable building blocks ----------------------------------------------

function PL({ children }) {
  // Italic styling for inline Polish terms - keeps the typography consistent.
  return <em className="italic text-slate-700">{children}</em>;
}

function Section({ id, n, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-14" data-testid={`section-${id}`}>
      <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mb-5">
        <span className="text-slate-400 mr-2 tabular-nums">{n}.</span>
        {title}
      </h2>
      <div className="space-y-4 text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

function Sub({ children }) {
  return <h3 className="font-display text-lg font-semibold text-slate-900 mt-6 mb-2">{children}</h3>;
}

function Warn({ children, testid }) {
  return (
    <aside
      className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4 flex items-start gap-3"
      data-testid={testid}
    >
      <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-amber-900 leading-relaxed">{children}</p>
    </aside>
  );
}

// -----------------------------------------------------------------------------

export default function Entrepreneuriat() {
  const { t } = useT();
  void t;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="entrepreneuriat-page">
      {/* Hero */}
      <header className="mb-12" data-testid="entrepreneuriat-hero">
        <span className="hej-chip" data-testid="entrepreneuriat-chip">
          <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
          Mis à jour juin 2026
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">
          Entreprendre en Pologne : statuts, fiscalité et cotisations
        </h1>
        <p className="text-slate-600 mt-4 leading-relaxed text-lg">
          Beaucoup de francophones installés en Pologne finissent par y travailler à leur compte, comme indépendants, consultants ou à
          la tête d'une petite société. Le cadre polonais est plus accessible qu'on ne l'imagine, mais il obéit à sa logique propre.
          Deux grandes formes juridiques dominent, trois régimes d'imposition coexistent pour les indépendants et un système de
          cotisations sociales, le ZUS, pèse dès le premier mois d'activité. Cette page fait le tour de ce qu'il faut comprendre avant
          de se lancer.
        </p>
      </header>

      {/* À retenir */}
      <section className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8 mb-12" data-testid="entrepreneuriat-key-points">
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">À retenir</h2>
        <ul className="space-y-3">
          {KEY_POINTS.map((point, i) => (
            <li key={i} className="flex items-start gap-3 text-slate-700 leading-relaxed">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#DC143C] flex-shrink-0" aria-hidden="true"></span>
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Sommaire */}
      <section className="mb-16" data-testid="entrepreneuriat-toc">
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">Sommaire</h2>
        <ol className="space-y-2 list-none">
          {TOC.map((item, i) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className="group flex items-baseline gap-3 py-2 px-3 rounded-lg hover:bg-slate-50 transition"
                data-testid={`toc-${item.id}`}
              >
                <span className="font-display text-sm font-semibold text-slate-400 group-hover:text-[#0056D2] tabular-nums w-6">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-slate-700 group-hover:text-[#0056D2] flex-1">{item.label}</span>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#0056D2] self-center" />
              </a>
            </li>
          ))}
        </ol>
      </section>

      {/* ============================================================ */}
      {/* Sections détaillées */}
      {/* ============================================================ */}

      <Section id="qui-peut" n={1} title="Qui peut entreprendre en Pologne">
        <p>
          La règle de base est favorable aux francophones. Les ressortissants de l'Union européenne et de l'Espace économique européen
          créent et exercent une activité en Pologne exactement comme les Polonais, sans autorisation préalable. Les citoyens suisses
          bénéficient de la même liberté grâce à l'accord sur la libre circulation des personnes entre la Suisse et l'Union européenne.
          Un Français ou un Suisse qui s'installe à Varsovie ou à Wrocław peut donc ouvrir une entreprise individuelle sans condition
          particulière liée à sa nationalité.
        </p>
        <p>
          La situation diffère pour les ressortissants d'autres pays. Hors de ce cercle, l'accès à l'entreprise individuelle dépend du
          titre de séjour détenu. Sans titre adéquat, la seule voie possible reste la création d'une société, le plus souvent une{" "}
          <PL>Sp. z o.o.</PL>
        </p>
        <p>
          Deux éléments pratiques conditionnent toute immatriculation, quelle que soit votre provenance : un numéro <PL>PESEL</PL> et
          une adresse en Pologne. Le <PL>PESEL</PL> s'obtient lors des premières démarches d'installation, que nous détaillons sur la
          page <Link to="/fr/demarches" className="text-[#0056D2] hover:underline">Démarches</Link>. L'adresse sert à rattacher
          l'entreprise à un bureau des impôts et à une caisse sociale.
        </p>
        <p>
          Avant de choisir le B2B, comparez-le au salariat classique: notre page{" "}
          <Link to="/fr/travailler-pologne" className="text-[#0056D2] hover:underline">Travailler en Pologne</Link>{" "}
          détaille les contrats de travail, les salaires réels et ce que le statut de salarié protège.
        </p>
      </Section>

      <Section id="jdg" n={2} title="L'entreprise individuelle (JDG)">
        <p>
          La <PL>jednoosobowa działalność gospodarcza</PL>, abrégée en <PL>JDG</PL>, est la forme la plus simple et la plus répandue.
          C'est l'équivalent fonctionnel du statut d'indépendant ou de l'entreprise individuelle française. Elle n'a pas de
          personnalité juridique distincte de la vôtre, ne demande aucun capital de départ et se crée en quelques jours.
        </p>
        <p>
          L'immatriculation se fait dans le registre <PL>CEIDG</PL> (<PL>Centralna Ewidencja i Informacja o Działalności Gospodarczej</PL>),
          en ligne sur ceidg.gov.pl ou biznes.gov.pl. La démarche est gratuite. Vous la signez avec un <PL>profil zaufany</PL>{" "}
          (identité numérique polonaise) ou une signature électronique qualifiée. L'inscription est en général effective sous sept
          jours, parfois le jour même. Le délai légal maximal est de trente jours. Les numéros <PL>NIP</PL> (identifiant fiscal) et{" "}
          <PL>REGON</PL> (identifiant statistique) vous sont attribués automatiquement.
        </p>
        <p>
          Au moment de l'inscription, vous renseignez la dénomination de l'entreprise, qui doit obligatoirement contenir vos prénom et
          nom, et un ou plusieurs codes d'activité <PL>PKD</PL> (la nomenclature polonaise des activités). L'affiliation au{" "}
          <PL>ZUS</PL> peut être transmise directement dans la procédure <PL>CEIDG</PL>. Si vous ne le faites pas à ce moment, vous
          disposez de sept jours pour déposer séparément le formulaire d'affiliation, <PL>ZUS ZUA</PL> ou <PL>ZUS ZZA</PL> selon votre
          situation.
        </p>
        <Warn testid="warn-jdg">
          <strong>À noter.</strong> L'entreprise individuelle n'offre aucune séparation entre votre patrimoine personnel et celui de
          l'activité. En cas de dettes professionnelles, vos biens personnels peuvent être engagés. C'est la principale différence
          avec une société à responsabilité limitée et le point à peser si votre activité comporte un risque financier réel.
        </Warn>
      </Section>

      <Section id="spzoo" n={3} title="La société à responsabilité limitée (Sp. z o.o.)">
        <p>
          La <PL>spółka z ograniczoną odpowiedzialnością</PL>, abrégée en <PL>Sp. z o.o.</PL>, correspond à la SARL. Elle possède une
          personnalité juridique propre et, surtout, la responsabilité des associés se limite en principe à leurs apports. Le
          patrimoine personnel est ainsi protégé, ce qui en fait le choix naturel dès que l'activité prend de l'ampleur ou implique
          des engagements importants.
        </p>
        <p>
          Le capital social minimal est de 5 000 zł, la valeur nominale d'une part ne pouvant être inférieure à 50 zł. Deux voies de
          constitution existent. La première passe par le système en ligne <PL>S24</PL>, à partir d'un modèle de statuts standardisé :
          comptez environ 350 zł de frais (250 zł d'inscription au registre et 100 zł de publication), pour une immatriculation très
          rapide. La seconde passe par un notaire (<PL>akt notarialny</PL>), nécessaire si vous voulez des statuts sur mesure : les
          frais d'inscription s'élèvent à 600 zł, auxquels s'ajoutent les honoraires notariés. Dans les deux cas, la société est
          inscrite au <PL>KRS</PL> (<PL>Krajowy Rejestr Sądowy</PL>), le registre national du commerce.
        </p>
        <p>
          Sur le plan fiscal, la société paie l'impôt sur les sociétés (<PL>CIT</PL>) : 9 % pour les petits contribuables (chiffre
          d'affaires sous 2 millions d'euros) et 19 % au-delà. La société doit tenir une comptabilité complète
          (<PL>pełna księgowość</PL>) et établir des comptes annuels, ce qui alourdit les frais de gestion par rapport à une{" "}
          <PL>JDG</PL>.
        </p>
        <p>
          Le sujet qui inquiète le plus les créateurs est la double imposition. La société acquitte d'abord le <PL>CIT</PL> sur son
          bénéfice, puis l'associé paie un impôt de 19 % sur les dividendes qu'il se verse. <strong className="text-slate-900">
          Un même bénéfice est donc taxé deux fois avant d'arriver dans la poche de l'associé.</strong> Le régime dit{" "}
          <PL>estoński CIT</PL> (CIT estonien) permet de différer l'imposition tant que le bénéfice reste dans la société, ce qui peut
          alléger la charge globale dans certaines configurations. C'est un arbitrage à examiner avec un comptable.
        </p>
        <Warn testid="warn-spzoo">
          <strong>À noter.</strong> Une <PL>Sp. z o.o.</PL> à associé unique réserve une mauvaise surprise. L'associé seul est
          assimilé, pour le <PL>ZUS</PL>, à une personne exerçant une activité et il paie donc des cotisations sociales pleines comme
          un indépendant. Cet effet disparaît dès qu'un second associé détient une part significative de la société.
        </Warn>
      </Section>

      <Section id="choisir" n={4} title="JDG ou Sp. z o.o. : comment choisir">
        <p>
          Il n'y a pas de réponse unique. La <PL>JDG</PL> gagne par sa simplicité, son coût nul et sa souplesse, au prix d'une
          responsabilité illimitée. La <PL>Sp. z o.o.</PL> protège le patrimoine et inspire davantage confiance aux partenaires et aux
          banques, mais coûte plus cher à créer et à faire vivre. En pratique, on commence souvent en <PL>JDG</PL>, puis on bascule
          vers une société quand les revenus, les risques ou les besoins de financement le justifient.
        </p>
        <div className="overflow-x-auto rounded-2xl border border-slate-200 mt-4">
          <table className="w-full text-sm" data-testid="entrepreneuriat-choose-table">
            <thead className="bg-slate-800 text-white">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Critère</th>
                <th className="px-4 py-3 text-left font-semibold">JDG (entreprise individuelle)</th>
                <th className="px-4 py-3 text-left font-semibold">Sp. z o.o. (société à responsabilité limitée)</th>
              </tr>
            </thead>
            <tbody>
              {CHOOSE_ROWS.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"} data-testid={`choose-row-${i}`}>
                  <td className="px-4 py-3 font-medium text-slate-900 border-t border-slate-200">{row[0]}</td>
                  <td className="px-4 py-3 text-slate-700 border-t border-slate-200">{row[1]}</td>
                  <td className="px-4 py-3 text-slate-700 border-t border-slate-200">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="regimes-fiscaux" n={5} title="Les régimes fiscaux de l'indépendant">
        <p>
          L'indépendant en <PL>JDG</PL> paie l'impôt sur le revenu (<PL>PIT</PL>) et choisit entre trois régimes. Le barème progressif
          est le régime par défaut et ne demande aucune démarche. Pour opter au contraire pour l'impôt linéaire ou le forfait, vous
          devez le déclarer au plus tard le 20e jour du mois qui suit celui de votre premier revenu de l'année (souvent le 20 février,
          mais plus tard si l'activité démarre en cours d'année), ou avant la fin de l'année si ce premier revenu tombe en décembre.
          Le choix vaut ensuite pour toute l'année. <strong className="text-slate-900">C'est l'une des décisions financières les plus
          importantes du démarrage,</strong> car elle détermine à la fois l'impôt et le calcul de la cotisation santé.
        </p>

        <Sub>5.1 Le barème progressif (<PL>skala podatkowa</PL>)</Sub>
        <p>
          Le barème progressif (<PL>skala podatkowa</PL>) est le régime par défaut. Il applique 12 % jusqu'à 120 000 zł de revenu
          annuel, puis 32 % au-delà. Il ouvre droit à une part non imposable (<PL>kwota wolna od podatku</PL>) de 30 000 zł et à la
          plupart des déductions, dont l'imposition commune avec le conjoint. Au-dessus d'un million de zł de revenu, une contribution
          de solidarité de 4 % s'ajoute. Ce régime convient bien aux revenus modérés et aux personnes qui bénéficient d'avantages
          familiaux.
        </p>

        <Sub>5.2 L'impôt linéaire (<PL>podatek liniowy</PL>)</Sub>
        <p>
          L'impôt linéaire (<PL>podatek liniowy</PL>) applique un taux unique de 19 %, quel que soit le revenu. En contrepartie, il ne
          donne droit ni à la part non imposable ni à l'imposition commune et limite les déductions. Il devient intéressant pour des
          revenus élevés et réguliers, lorsque l'on dépasse nettement le premier palier du barème et que l'on a peu d'avantages
          fiscaux à faire valoir.
        </p>

        <Sub>5.3 Le forfait sur le chiffre d'affaires (<PL>ryczałt</PL>)</Sub>
        <p>
          Le forfait sur le chiffre d'affaires (<PL>ryczałt od przychodów ewidencjonowanych</PL>) taxe les recettes brutes, sans
          déduction des charges, à un taux qui dépend de l'activité et s'échelonne de 2 à 17 %. Le plafond de chiffre d'affaires pour
          y rester éligible est d'environ 2 millions d'euros. C'est souvent le régime le plus avantageux pour les activités de
          services à faibles charges, par exemple dans le conseil ou l'informatique, à condition de vérifier le taux applicable à
          votre code d'activité.
        </p>
      </Section>

      <Section id="zus" n={6} title="Les cotisations sociales (ZUS)">
        <p>
          Tout entrepreneur cotise au <PL>ZUS</PL> (<PL>Zakład Ubezpieczeń Społecznych</PL>), la caisse de sécurité sociale. Les
          cotisations sociales sont en grande partie forfaitaires, ce qui change la donne par rapport à un système assis sur le revenu
          réel : <strong className="text-slate-900">elles sont dues même les mois creux.</strong>
        </p>
        <p>
          En 2026, le régime plein (le « grand ZUS ») repose sur une base de 5 652,60 zł, soit 60 % du salaire moyen prévisionnel. Les
          cotisations sociales (retraite, invalidité, accident, maladie facultative et Fonds du travail) atteignent environ 1 927 zł
          par mois, ou environ 1 788 zł si vous renoncez à l'assurance maladie facultative. À ce montant s'ajoute la cotisation santé
          (<PL>składka zdrowotna</PL>), distincte.
        </p>

        <Sub>6.1 Les allègements pour les débutants</Sub>
        <p>
          Les débutants bénéficient d'allègements substantiels. La première mesure, l'<PL>ulga na start</PL>, exonère de cotisations
          sociales pendant les six premiers mois (seule la cotisation santé reste due). Viennent ensuite les cotisations préférentielles
          (<PL>preferencyjne składki</PL>), pendant vingt-quatre mois, calculées sur une base réduite de 1 441,80 zł, soit environ
          456 zł par mois de cotisations sociales. Le <PL>Mały ZUS Plus</PL>, à ne pas confondre avec ce régime préférentiel, est un
          dispositif distinct et soumis à conditions, qui ajuste ensuite les cotisations au revenu réel.
        </p>

        <Sub>6.2 La cotisation santé (<PL>składka zdrowotna</PL>)</Sub>
        <p>
          La cotisation santé suit ses propres règles et dépend du régime fiscal. Au barème, elle représente 9 % du revenu, avec un
          minimum de 432,54 zł par mois en 2026. À l'impôt linéaire, elle est de 4,9 % du revenu, avec le même plancher. Au forfait,
          elle est fixée par tranches de chiffre d'affaires : 498,35 zł, 830,58 zł ou 1 495,04 zł par mois selon que vos recettes
          annuelles restent sous 60 000 zł, se situent entre 60 000 et 300 000 zł, ou dépassent ce seuil. Les cotisations se règlent
          au plus tard le 20 de chaque mois, via la déclaration <PL>ZUS DRA</PL>.
        </p>

        <Warn testid="warn-zus">
          <strong>À noter.</strong> Le <PL>ZUS</PL> est un coût quasiment fixe, largement indépendant de votre bénéfice. Un mois sans
          chiffre d'affaires reste un mois de cotisations. Intégrez ce poste dans votre budget dès le départ, en gardant à l'esprit
          que les montants sont réévalués chaque année. Pour estimer le reste à vivre, la page{" "}
          <Link to="/fr/cout-de-la-vie" className="underline text-amber-900 hover:text-amber-950">Coût de la vie</Link> donne des
          repères ville par ville.
        </Warn>
      </Section>

      <Section id="tva" n={7} title="La TVA et la facturation électronique">
        <p>
          La taxe sur la valeur ajoutée (<PL>VAT</PL>) polonaise a un taux normal de 23 %, avec des taux réduits de 8 %, 5 % et 0 %
          selon les biens et services. L'enregistrement se fait au moyen du formulaire <PL>VAT-R</PL>, que l'on peut joindre
          directement à la demande d'immatriculation <PL>CEIDG-1</PL>.
        </p>
        <p>
          Une franchise existe pour les petites structures. En dessous d'un certain chiffre d'affaires annuel, vous pouvez bénéficier
          d'une exonération (<PL>zwolnienie podmiotowe</PL>) et ne pas facturer la <PL>VAT</PL>. Le seuil, longtemps fixé à 200 000 zł,
          passe à 240 000 zł à partir de 2026, avec des exceptions selon l'activité.
        </p>
        <p>
          La Pologne a par ailleurs généralisé la facturation électronique via le <PL>KSeF</PL>{" "}
          (<PL>Krajowy System e-Faktur</PL>), selon un calendrier par étapes. L'émission de factures dans ce système est obligatoire
          depuis le 1er février 2026 pour les grandes entreprises (plus de 200 millions de zł de ventes en 2024) et depuis le
          1er avril 2026 pour les autres, la réception y étant obligatoire pour tous depuis février 2026. Une tolérance transitoire
          subsiste jusqu'à la fin 2026 pour les très petits volumes de facturation. La généralisation complète, jusqu'aux plus petits
          acteurs, est prévue pour 2027. En pratique, <strong className="text-slate-900">le <PL>KSeF</PL> devient le canal de
          facturation standard, autant s'y préparer dès la création de l'entreprise.</strong>
        </p>
      </Section>

      <Section id="convention" n={8} title="La convention fiscale franco-polonaise">
        <p>
          La France et la Pologne sont liées par une convention destinée à éviter la double imposition, signée à Varsovie le 20 juin
          1975. Elle reste en vigueur aujourd'hui, modernisée par l'instrument multilatéral de l'OCDE que les deux pays ont signé en
          2017 et qui produit ses effets depuis 2019. Il n'existe pas, à ce jour, de nouvelle convention bilatérale qui la
          remplacerait.
        </p>
        <p>
          Le principe est simple à énoncer. <strong className="text-slate-900">Un même revenu ne doit pas être taxé deux fois, une
          fois en France et une fois en Pologne.</strong> La convention répartit le droit d'imposer entre les deux États selon la
          nature du revenu, salaires, bénéfices d'entreprise, dividendes, intérêts, redevances ou revenus immobiliers, et prévoit un
          mécanisme pour effacer la double imposition résiduelle. Elle abaisse aussi les retenues à la source sur les dividendes,
          intérêts et redevances par rapport aux taux applicables à un pays non conventionné.
        </p>
        <p>
          Deux notions méritent l'attention de l'entrepreneur. La première est l'établissement stable : une activité menée en Pologne
          au travers d'une installation fixe y devient imposable, même si l'entreprise est française. La seconde est le certificat
          de résidence fiscale, document que l'administration de votre pays de résidence vous délivre et qui conditionne l'application
          de la convention. Sans lui, l'administration polonaise applique son régime de droit commun.
        </p>
        <Warn testid="warn-convention">
          <strong>À noter.</strong> Cette page traite de la convention franco-polonaise parce qu'elle concerne une large part du
          lectorat. Si vous résidez fiscalement en Suisse, c'est la convention entre la Suisse et la Pologne qui s'applique, avec ses
          propres règles. Le principe d'élimination de la double imposition est comparable, mais les détails diffèrent et méritent une
          vérification au cas par cas.
        </Warn>
      </Section>

      <Section id="residence-fiscale" n={9} title="La résidence fiscale">
        <p>
          Savoir où vous êtes résident fiscal détermine où vous payez l'impôt sur l'ensemble de vos revenus. En Pologne, vous êtes
          considéré comme résident si vous y avez le centre de vos intérêts personnels ou économiques, ou si vous y séjournez plus de
          183 jours dans l'année. <strong className="text-slate-900">Le résident est imposé sur ses revenus mondiaux, le non-résident
          seulement sur ses revenus de source polonaise.</strong>
        </p>
        <p>
          Le point sensible survient l'année du déménagement, quand vous pouvez relever de deux administrations à la fois. C'est
          précisément le rôle de la convention de trancher, à l'aide de critères en cascade (foyer permanent, centre des intérêts
          vitaux, lieu de séjour habituel, nationalité) et, en dernier recours, d'une procédure amiable entre les deux États. Une
          particularité polonaise à connaître : la notion de foyer fiscal n'existe pas, les époux sont imposés individuellement et
          l'imposition commune doit être demandée.
        </p>
      </Section>

      <Section id="premieres-demarches" n={10} title="Premières démarches concrètes">
        <p>
          L'ordre des étapes compte. On obtient d'abord son <PL>PESEL</PL> et, selon sa provenance, le titre de séjour requis, sujets
          traités sur la page <Link to="/fr/demarches" className="text-[#0056D2] hover:underline">Démarches</Link>. On choisit ensuite
          sa forme juridique et son régime fiscal, idéalement après un échange avec un comptable. On procède à l'immatriculation,
          dans <PL>CEIDG</PL> pour une <PL>JDG</PL> ou dans le <PL>KRS</PL> pour une <PL>Sp. z o.o.</PL>, on se déclare au{" "}
          <PL>ZUS</PL> et on ouvre un compte bancaire professionnel. Enfin, on met en place sa facturation, désormais reliée au{" "}
          <PL>KSeF</PL>.
        </p>
        <p>
          Un point vaut d'être souligné pour finir. <strong className="text-slate-900">Le comptable, le <PL>księgowy</PL>, n'est pas
          un luxe en Pologne mais une quasi-nécessité,</strong> ne serait-ce que pour arbitrer entre les régimes, suivre les échéances{" "}
          <PL>ZUS</PL> et tenir une comptabilité conforme. Son coût est modeste au regard des erreurs qu'il évite, surtout les
          premiers mois.
        </p>
      </Section>

      {/* Sources */}
      <section id="sources" className="scroll-mt-24 mb-12" data-testid="entrepreneuriat-sources">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mb-5">Sources</h2>
        <ol className="space-y-5 list-none">
          {SOURCES.map((s, i) => (
            <li key={s.url} className="flex items-baseline gap-3" data-testid={`source-${i + 1}`}>
              <span className="font-display text-sm font-semibold text-slate-400 tabular-nums w-6 flex-shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="flex-1">
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-start gap-1.5 text-[#0056D2] hover:underline break-words"
                  data-testid={`source-link-${i + 1}`}
                >
                  <span>{s.label}</span>
                  <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 mt-1" />
                </a>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Final disclaimer */}
      <Warn testid="warn-final">
        Cette page a une vocation informative et ne remplace pas un conseil juridique ou comptable personnalisé. La fiscalité et les
        cotisations polonaises évoluent chaque année et votre situation (provenance, type d'activité, niveau de revenu) modifie
        l'analyse. Avant de créer votre structure, faites valider votre choix par un comptable polonais ou un conseiller fiscal. Les
        taux du <PL>ZUS</PL>, les règles de la cotisation santé, le seuil de franchise de <PL>TVA</PL> et le calendrier du{" "}
        <PL>KSeF</PL> sont les éléments à revérifier le plus régulièrement. Mis à jour en juin 2026.
      </Warn>
    </div>
  );
}
